import { CommonModule } from '@angular/common';
import { Component, Input, OnInit, Optional, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { finalize } from 'rxjs';
import { DynamicFormComponent } from 'src/app/components/dynamic-form/dynamic-form.component';
import { LoadingIconComponent } from 'src/app/components/loading-icon/loading-icon.component';
import { IconModule } from 'src/app/icon/icon.module';
import { MaterialModule } from 'src/app/material.module';
import { ProcessService } from 'src/app/services/process.service';
import { SystemService } from 'src/app/services/system.service';
import { ToastService } from 'src/app/services/toast.service';
import { PATTERN } from 'src/app/shared/constants';
import { FormControlType, SCREEN_SIZE } from 'src/app/shared/enums';
import { IFormConfig, ILayout } from 'src/app/shared/interfaces/IForm';

@Component({
  selector: 'app-process-general-information',
  imports: [DynamicFormComponent, IconModule, MaterialModule, TranslateModule, CommonModule, LoadingIconComponent],
  templateUrl: './process-general-information.component.html',
  styleUrls: ['./process-general-information.component.scss']
})
export class ProcessGeneralInformationComponent implements OnInit {
  @ViewChild(DynamicFormComponent) dynamicForm!: DynamicFormComponent;
  @Input() process: any;
  formConfigs: IFormConfig;
  processForm: FormGroup;
  isLoading: boolean;
  processId: number;
  isReadOnly: boolean;
  private readonly quarterLayout: ILayout[] = [
    { column: 12 },
    { column: 6, screenSize: SCREEN_SIZE.SMALL },
    { column: 3, screenSize: SCREEN_SIZE.LARGE }
  ];

  constructor(
    @Optional() private dialogRef: MatDialogRef<ProcessGeneralInformationComponent>,
    private formBuilder: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private translateService: TranslateService,
    private toastService: ToastService,
    private systemService: SystemService,
    private processService: ProcessService) {
    this.processId = Number(this.route.snapshot.parent?.params['processId'] || this.route.snapshot.params['processId']);
    this.isReadOnly = !this.router.url.startsWith('/admin');
  }

  ngOnInit(): void {
    this.initForm();
    this.loadData();
  }

  loadData() {
    if (!this.processId) return;
    this.isLoading = true;
    this.processService.getProcess(this.processId).subscribe({
      next: (res: any) => {
        this.process.data = res;
        this.processForm.patchValue(res);
        this.isLoading = false;
      },
      error: (error) => {
        console.error(error);
        this.toastService.error(this.translateService.instant('error loading data'));
        this.isLoading = false;
      }
    });
  }

  initForm() {
    const defaultValue = { value: null, disabled: this.isReadOnly };
    this.processForm = this.formBuilder.group({
      id: [null],
      datasetMainName: [null, [Validators.required, Validators.maxLength(100)]],   
      dataSourceId: [defaultValue],
      datasetUnitId: [defaultValue],
      dataProviderId: [defaultValue],
      marketMix: [null, [Validators.maxLength(50)]],
      description: [null, [Validators.maxLength(7000)]],
      yearOfPublication: [null, [Validators.pattern(PATTERN.NUMBER), Validators.min(0), Validators.max(9999)]],
      dryMatterContent: [null, [Validators.pattern(PATTERN.DECIMAL_13_9)]],
      energyContent: [null, [Validators.pattern(PATTERN.DECIMAL_13_9)]],
      nitrogenContent: [null, [Validators.pattern(PATTERN.DECIMAL_13_9)]],
      phosphorusContent: [null, [Validators.pattern(PATTERN.DECIMAL_13_9)]],
      dqrtotal: [null, [Validators.pattern(PATTERN.DECIMAL_24_12)]],
      dqrtime: [null, [Validators.pattern(PATTERN.DECIMAL_24_12)]],
      dqrtech: [null, [Validators.pattern(PATTERN.DECIMAL_24_12)]],
      dqrgeo: [null, [Validators.pattern(PATTERN.DECIMAL_24_12)]],
      dqrprecision: [null, [Validators.pattern(PATTERN.DECIMAL_24_12)]]
    });

    this.formConfigs = {
      id: 'processDetail',
      items: [
        {
          key: 'datasetMainName',
          label: 'name',
          layoutConfig: [
            { column: 12 },
            { column: 6, screenSize: SCREEN_SIZE.MEDIUM }
          ],
          controlType: FormControlType.INPUT,
          appearance: this.isReadOnly ? 'fill' : 'outline',
          inputConfig: { readonly: this.isReadOnly },
          validator: [
            { errorType: 'required', errorMessage: 'name required' },
            { errorType: 'maxlength', errorMessage: 'name is too long', params: { length: 100 } }
          ]
        },     
        {
          key: 'dataSourceId',
          label: 'data source',
          layoutConfig: this.quarterLayout,
          controlType: FormControlType.AUTOCOMPLETE,
          appearance: this.isReadOnly ? 'fill' : 'outline',
          fetchDataAPI: () => this.systemService.getDataSources({ pageSize: 1000 }),
          inputConfig: { readonly: this.isReadOnly },
          selectionConfig: {
            title: 'name',
            key: 'id'
          }
        },
        {
          key: 'datasetUnitId',
          label: 'dataset unit',
          layoutConfig: this.quarterLayout,
          controlType: FormControlType.AUTOCOMPLETE,
          appearance: this.isReadOnly ? 'fill' : 'outline',
          fetchDataAPI: () => this.systemService.getDatasetUnits({ pageSize: 1000 }),
          inputConfig: { readonly: this.isReadOnly },
          selectionConfig: {
            title: 'name',
            key: 'id'
          }
        },
        {
          key: 'dataProviderId',
          label: 'data provider',
          layoutConfig: this.quarterLayout,
          controlType: FormControlType.SELECT,
          appearance: this.isReadOnly ? 'fill' : 'outline',
          fetchDataAPI: () => this.systemService.getDataProviders({ pageSize: 1000 }),
          inputConfig: { readonly: this.isReadOnly },
          selectionConfig: {
            title: 'name',
            key: 'id'
          }
        },
        {
          key: 'yearOfPublication',
          label: 'year of publication',
          layoutConfig: this.quarterLayout,
          controlType: FormControlType.INPUT,
          appearance: this.isReadOnly ? 'fill' : 'outline',
          inputConfig: { type: 'number', readonly: this.isReadOnly },
          validator: [
            { errorType: 'min', errorMessage: 'year of publication must be greater than', params: { min: -1 } },
            { errorType: 'max', errorMessage: 'year of publication must be less than', params: { max: 10000 } }
          ]
        },
        {
          key: 'dryMatterContent',
          label: 'dry matter content',
          layoutConfig: this.quarterLayout,
          controlType: FormControlType.INPUT,
          appearance: this.isReadOnly ? 'fill' : 'outline',
          inputConfig: { type: 'number', readonly: this.isReadOnly },
          validator: [{ errorType: 'pattern', errorMessage: 'dry matter content must be less than', params: { max: 10000 } }]
        },
        {
          key: 'energyContent',
          label: 'energy content',
          layoutConfig: this.quarterLayout,
          controlType: FormControlType.INPUT,
          appearance: this.isReadOnly ? 'fill' : 'outline',
          inputConfig: { type: 'number', readonly: this.isReadOnly },
          validator: [{ errorType: 'pattern', errorMessage: 'energy content must be less than', params: { max: 10000 } }]
        },
        {
          key: 'nitrogenContent',
          label: 'nitrogen content',
          layoutConfig: this.quarterLayout,
          controlType: FormControlType.INPUT,
          appearance: this.isReadOnly ? 'fill' : 'outline',
          inputConfig: { type: 'number', readonly: this.isReadOnly },
          validator: [{ errorType: 'pattern', errorMessage: 'nitrogen content must be less than', params: { max: 10000 } }]
        },
        {
          key: 'phosphorusContent',
          label: 'phosphorus content',
          layoutConfig: this.quarterLayout,
          controlType: FormControlType.INPUT,
          appearance: this.isReadOnly ? 'fill' : 'outline',
          inputConfig: { type: 'number', readonly: this.isReadOnly },
          validator: [{ errorType: 'pattern', errorMessage: 'phosphorus content must be less than', params: { max: 10000 } }]
        },
        {
          key: 'dqrtotal',
          label: 'dqr total',
          layoutConfig: this.quarterLayout,
          controlType: FormControlType.INPUT,
          appearance: this.isReadOnly ? 'fill' : 'outline',
          inputConfig: { type: 'number', readonly: this.isReadOnly },
          validator: [{ errorType: 'pattern', errorMessage: 'dqr total must be less than', params: { max: 1000000000000 } }]
        },
        {
          key: 'dqrprecision',
          label: 'dqr precision',
          layoutConfig: this.quarterLayout,
          controlType: FormControlType.INPUT,
          appearance: this.isReadOnly ? 'fill' : 'outline',
          inputConfig: { type: 'number', readonly: this.isReadOnly },
          validator: [{ errorType: 'pattern', errorMessage: 'dqr precision must be less than', params: { max: 1000000000000 } }]
        },
        {
          key: 'dqrtime',
          label: 'dqr time',
          layoutConfig: this.quarterLayout,
          controlType: FormControlType.INPUT,
          appearance: this.isReadOnly ? 'fill' : 'outline',
          inputConfig: { type: 'number', readonly: this.isReadOnly },
          validator: [{ errorType: 'pattern', errorMessage: 'dqr time must be less than', params: { max: 1000000000000 } }]
        },
        {
          key: 'dqrtech',
          label: 'dqr tech',
          layoutConfig: this.quarterLayout,
          controlType: FormControlType.INPUT,
          appearance: this.isReadOnly ? 'fill' : 'outline',
          inputConfig: { type: 'number', readonly: this.isReadOnly },
          validator: [{ errorType: 'pattern', errorMessage: 'dqr tech must be less than', params: { max: 1000000000000 } }]
        },
        {
          key: 'dqrgeo',
          label: 'dqr geo',
          layoutConfig: this.quarterLayout,
          controlType: FormControlType.INPUT,
          appearance: this.isReadOnly ? 'fill' : 'outline',
          inputConfig: { type: 'number', readonly: this.isReadOnly },
          validator: [{ errorType: 'pattern', errorMessage: 'dqr geo must be less than', params: { max: 1000000000000 } }]
        },
        {
          key: 'marketMix',
          label: 'market mix',
          layoutConfig: this.quarterLayout,
          controlType: FormControlType.INPUT,
          appearance: this.isReadOnly ? 'fill' : 'outline',
          inputConfig: { readonly: this.isReadOnly },
          validator: [{ errorType: 'maxlength', errorMessage: 'market mix is too long', params: { length: 50 } }]
        },
        {
          key: 'description',
          label: 'description',
          layoutConfig: [{ column: 12 }],
          controlType: FormControlType.TEXTAREA,
          appearance: this.isReadOnly ? 'fill' : 'outline',
          inputConfig: { textareaRows: 10, readonly: this.isReadOnly },
          validator: [{ errorType: 'maxlength', errorMessage: 'description is too long', params: { length: 7000 } }]
        }
      ]
    };
  }

  handleUpdateProcess = () => {
    if (this.processForm.invalid) return this.processForm.markAllAsTouched();

    this.isLoading = true;
    this.processService.addOrUpdateProcess(this.processForm.value).pipe(
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe({
        next: (res: any) => {
          this.toastService.success(this.translateService.instant(this.processId ? 'update successfully' : 'add successfully'));
          if (!this.processId) {
            this.onClose(res);
          }
        },
        error: ({ error }) => {
          this.dynamicForm.ValidatorError = error;
        }
      });
  };

  onClose(data?: any) {
    this.dialogRef.close(data);
  }
}
